import { computed, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { pipe, switchMap, tap } from 'rxjs';
import {
  setPropError,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { ErrorStore } from '@application/errors/data-access';
import {
  AcademyCapabilitiesState,
  initialAcademyCapabilitiesState,
} from './entities/academy-capabilities-store.model';
import { CapabilitiesService } from './infra/capabilities.service';

/** Alert body when `GET /Capabilities/me` fails and the server sent no message. */
const LOAD_ERROR_MESSAGE = 'ACADEMY.CAPABILITIES.LOAD_ERROR';

/**
 * Capabilities of the signed-in person in the Academy (`GET /Capabilities/me`). The
 * producer guard, the sidebar and the producer bits of the Academy pages read these
 * flags instead of a local list of producer tenants.
 *
 * Loaded once per session through `ensureCapabilitiesLoaded()`. Every account switch
 * ends in a full page reload (`switch-account-animation.component.ts`, after
 * `SelfUserStore.clearSelfUserStore()`), which recreates this root store for the new
 * tenant and person — the same lifecycle `SelfUserStore` relies on.
 *
 * Every flag reads `false` until the answer arrives, and stays `false` on error: the
 * producer screens fail closed. A failure is shown once per outage — any status,
 * bodiless 502/503/504 included — and retries of the same outage stay silent until
 * a load succeeds again.
 */
export const AcademyCapabilitiesStore = signalStore(
  { providedIn: 'root' },
  withState<AcademyCapabilitiesState>(initialAcademyCapabilitiesState),
  withReqState('loadCapabilities'),
  withComputed((store) => ({
    isStaff: computed<boolean>(() => store.capabilities()?.IsStaff === true),
    isPlatformTenant: computed<boolean>(
      () => store.capabilities()?.IsPlatformTenant === true
    ),
    canProducePlatformContent: computed<boolean>(
      () => store.capabilities()?.CanProducePlatformContent === true
    ),
  })),
  withMethods(
    (
      store,
      capabilitiesService = inject(CapabilitiesService),
      errorStore = inject(ErrorStore)
    ) => {
      const loadCapabilities = rxMethod<void>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadCapabilities'))),
          switchMap(() =>
            capabilitiesService.me().pipe(
              tapResponse({
                next: (capabilities) =>
                  patchState(
                    store,
                    {
                      capabilities,
                      capabilitiesSettled: true,
                      capabilitiesErrorReported: false,
                    },
                    setPropLoaded('loadCapabilities')
                  ),
                error: (error: HttpErrorResponse) => {
                  // `handleError` with an explicit message, not `setHttpError`: the
                  // latter only maps 0/400/401/403/404/500, so a bodiless 502/503/504
                  // would hide the producer menus and routes without a word.
                  if (!store.capabilitiesErrorReported()) {
                    errorStore.handleError({ error, message: LOAD_ERROR_MESSAGE });
                  }
                  // Drop the previous answer: a producer whose reload fails must not
                  // keep `canProducePlatformContent` true next to the error.
                  patchState(
                    store,
                    {
                      capabilities: null,
                      capabilitiesSettled: true,
                      capabilitiesErrorReported: true,
                    },
                    setPropError('loadCapabilities', error)
                  );
                },
              })
            )
          )
        )
      );

      /**
       * Starts the load unless it already succeeded or is in flight. Idempotent, so
       * the guard, the sidebar and the pages can all call it without cancelling each
       * other's request. A failed load is retried on the next call; the retry's
       * failure is not shown again (see `capabilitiesErrorReported`).
       */
      const ensureCapabilitiesLoaded = (): void => {
        if (store.loadCapabilitiesLoaded() || store.loadCapabilitiesLoading()) {
          return;
        }
        loadCapabilities();
      };

      return { loadCapabilities, ensureCapabilitiesLoaded };
    }
  )
);
